import { ReactComponent as SelectIcon } from "../../../common/assets/icons/selectIcon.svg";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type SelectBoxProps = {
    status?: string;
    className?: string;
    options?: { value: string; label: string }[];
    onChange?: (value: string) => void;
    onSearchChange?: (searchText: string) => void;
    placeholder?: string;
    value?: string;
    searchable?: boolean;
    label?: string;
    full?: boolean;
    disabled?: boolean;
    height?: string;
    length?: number;
    focusPlaceHolder?: string;
};

const PortalSelectBox = ({
    status,
    className,
    options = [],
    onChange,
    onSearchChange,
    placeholder,
    value,
    searchable = false,
    label,
    full,
    disabled,
    height,
    length,
    focusPlaceHolder,
}: SelectBoxProps) => {
    console.log(options);
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [selectedOption, setSelectedOption] = useState<{ value: string; label: string } | undefined>(
        options.find(option => option.value === value),
    );
    const selectBoxRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number }>({
        top: 0,
        left: 0,
        width: 0,
    });

    const filteredOptions = options;
    const displayLabel = selectedOption ? selectedOption.label : null;

    const handleSelect = (selectedValue: string) => {
        if (disabled) return;
        setSearchText("");
        setIsOpen(false);
        const foundOption = options.find(option => option.value === selectedValue);
        if (foundOption) {
            setSelectedOption(foundOption);
        }
        onChange?.(selectedValue);
    };

    const handleClickOutside = (event: MouseEvent) => {
        if (
            selectBoxRef.current &&
            !selectBoxRef.current.contains(event.target as Node) &&
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target as Node)
        ) {
            setIsOpen(false);
        }
    };

    const updateDropdownPosition = () => {
        if (selectBoxRef.current) {
            const rect = selectBoxRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
            });
        }
    };

    useEffect(() => {
        const foundOption = options.find(option => option.value === value);
        if (foundOption?.value !== selectedOption?.value) {
            setSelectedOption(foundOption);
        }
    }, [value, options]);

    useEffect(() => {
        if (value === undefined || value === null || value === "") {
            setSelectedOption(undefined);
        }
    }, [value]);

    useEffect(() => {
        if (searchable && onSearchChange) {
            onSearchChange(searchText);
        }
    }, [searchText]);

    useEffect(() => {
        if (isOpen) {
            updateDropdownPosition();
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    return (
        <div className={`relative rounded-lg ${className}`} ref={selectBoxRef}>
            <div
                className={`flex items-center justify-between rounded-lg ${disabled ? "cursor-default bg-gray6 !border-none" : "cursor-pointer"} ${
                    status === "focused"
                        ? "border-primary1 py-1.5 px-2.5"
                        : status === "error"
                          ? "border-error border py-1.5 px-2.5"
                          : status === "none"
                            ? "bg-gray6 py-1.5 px-2.5"
                            : status === "view"
                              ? ""
                              : "border-gray4 border py-1.5 px-2.5"
                } ${isOpen ? "border-primary1" : ""}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                <span className={`text-sm ${displayLabel || label ? "text-gray2" : "text-grayicon"}`}>
                    {label
                        ? !full && label?.length > (length || 7)
                            ? label?.slice(0, length || 7) + "..."
                            : label
                        : displayLabel
                          ? !full && displayLabel.length > 7
                              ? `${displayLabel.slice(0, 7)}...`
                              : displayLabel
                          : isOpen
                            ? focusPlaceHolder
                            : placeholder}
                </span>

                <SelectIcon className={`transform ${isOpen ? "rotate-180" : ""}`} />
            </div>

            {isOpen &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        className={`absolute p-2 text-sm bg-white border overflow-y-auto  border-gray4 rounded-lg shadow-lg z-10 selectbox-dropdown`}
                        style={{
                            top: dropdownPosition.top,
                            left: dropdownPosition.left,
                            width: dropdownPosition.width,
                        }}
                    >
                        {searchable && (
                            <input
                                type="text"
                                className="w-full px-2  py-2 mb-2 border border-gray4 rounded-lg outline-none text-sm"
                                placeholder="검색"
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                autoFocus
                            />
                        )}

                        <div className={`${height} overflow-y-auto`}>
                            {filteredOptions.length > 0 ? (
                                filteredOptions.map(option => (
                                    <div
                                        key={option.value}
                                        className={`px-3 rounded-lg py-2 cursor-pointer ${
                                            selectedOption?.value === option.value
                                                ? "bg-primary4 text-primary2 pointer-events-none"
                                                : "hover:bg-primary3 text-primary2"
                                        }`}
                                        onClick={() => handleSelect(option.value)}
                                    >
                                        {option.label}
                                    </div>
                                ))
                            ) : (
                                <p className="text-center text-grayicon py-2">검색 결과가 없습니다.</p>
                            )}
                        </div>
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export default PortalSelectBox;
